import { useEffect, useState } from "react";
import { KeyRound, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { AdminVoicePicker } from "@/components/admin/AdminVoicePicker";

const Field = ({ label, hint, testId, ...props }) => (
  <label className="block">
    <span className="text-sm font-semibold">{label}</span>
    <input {...props} data-testid={testId} className="mt-1.5 h-11 w-full rounded-xl border border-[#e7e7ea] bg-[#F5F5F7] px-3.5 text-sm outline-none focus:border-[#0066FF] focus:bg-white" />
    {hint && <span className="mt-1 block text-xs text-[#52525b]">{hint}</span>}
  </label>
);

export const AdminIntegrations = ({ headers }) => {
  const [info, setInfo] = useState(null);
  const [key, setKey] = useState("");
  const [giulia, setGiulia] = useState("");
  const [marco, setMarco] = useState("");
  const [busy, setBusy] = useState(false);

  const apply = (d) => {
    setInfo(d);
    setGiulia(d?.voce_giulia_id ?? "");
    setMarco(d?.voce_marco_id ?? "");
  };

  useEffect(() => {
    api.get("/admin/integrations", { headers }).then((r) => apply(r.data)).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const save = async (extra = {}) => {
    setBusy(true);
    try {
      const { data } = await api.put("/admin/integrations", { elevenlabs_api_key: key || null, voce_giulia_id: giulia, voce_marco_id: marco, ...extra }, { headers });
      apply(data);
      setKey("");
      if (data?.elevenlabs_verifica && data.elevenlabs_verifica !== "ok" && !extra.rimuovi_elevenlabs) toast.error(`Chiave ElevenLabs non valida (${data.elevenlabs_verifica})`);
      else toast.success("Impostazioni salvate");
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  const ok = info?.elevenlabs_configurata && info?.elevenlabs_verifica === "ok";
  return (
    <div className="card-soft p-6 hover:translate-y-0 sm:p-8" data-testid="admin-integrations">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="font-display flex items-center gap-2 text-lg font-bold"><KeyRound className="h-5 w-5 text-[#0066FF]" /> Chiavi API di MIMIS Video</p>
        <span className={`rounded-full px-3 py-1.5 text-xs font-semibold ${ok ? "bg-emerald-50 text-emerald-700" : "bg-amber-50 text-amber-800"}`} data-testid="elevenlabs-status">
          {ok ? `ElevenLabs attiva (${info.elevenlabs_mascherata})` : info?.elevenlabs_configurata ? "ElevenLabs: chiave da verificare" : "ElevenLabs non collegata: voce di riserva OpenAI"}
        </span>
      </div>
      <p className="mt-2 text-sm text-[#52525b]">Script e immagini: {info?.openai ?? "—"}. Le chiavi restano sul server e non vengono mai mostrate per intero.</p>
      {info?.elevenlabs_configurata && !ok && (
        <p className="mt-3 rounded-xl bg-amber-50 p-3 text-sm text-amber-900" data-testid="elevenlabs-error">
          ElevenLabs ha risposto: {info.elevenlabs_verifica}. Finché non è risolto i video usano la voce di riserva OpenAI.
        </p>
      )}
      <div className="mt-6 grid gap-5 md:grid-cols-3">
        <Field label="Chiave API ElevenLabs" type="password" autoComplete="off" value={key} onChange={(e) => setKey(e.target.value)}
          placeholder={info?.elevenlabs_configurata ? info.elevenlabs_mascherata : "Incolla qui la chiave"} hint="La trovi su elevenlabs.io → Settings → API Keys" testId="elevenlabs-key-input" />
        <Field label="ID voce Giulia" value={giulia} onChange={(e) => setGiulia(e.target.value)} placeholder={info?.voce_giulia_default} hint="Scegli una voce italiana nella Voice Library" testId="voice-giulia-input" />
        <Field label="ID voce Marco" value={marco} onChange={(e) => setMarco(e.target.value)} placeholder={info?.voce_marco_default} hint="Vuoto = voce predefinita" testId="voice-marco-input" />
      </div>
      <div className="mt-6 flex flex-wrap gap-3">
        <button onClick={() => save()} disabled={busy} className="btn-blu h-11 px-6 text-sm" data-testid="save-integrations-button">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Salva"}</button>
        {info?.elevenlabs_fonte === "admin" && (
          <button onClick={() => save({ rimuovi_elevenlabs: true })} disabled={busy} className="btn-ghost h-11 px-5 text-sm" data-testid="remove-elevenlabs-button">Rimuovi chiave</button>
        )}
      </div>
      {ok && <AdminVoicePicker headers={headers} info={info} onChange={apply} />}
    </div>
  );
};
